import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X, Loader2, Users, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  useGroupSession,
  useGroupSessionParticipants,
  useCreateGroupSession,
  useUpdateGroupSession,
  useDeleteGroupSession,
  useAddParticipant,
  useRemoveParticipant,
  type GroupSessionStatus,
} from '@/hooks/useGroupSessions';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicHoursExceptions } from '@/hooks/useClinicHoursExceptions';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { SERVICE_DURATION_MAX } from '@/lib/serviceDuration';
import {
  civilDateFromLocalDate,
  exceptionByDate,
  isClosedHoursException,
  localIntervalFitsResolvedHours,
  parseWeeklyHours,
  resolveClinicHoursForLocalDate,
} from '@/lib/clinicHours';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

export interface GroupSessionModalProps {
  clinicId: string;
  mode: 'create' | 'edit';
  sessionId?: string | null;
  /** Initial slot when creating (clicked time slot in the calendar). */
  initialStart?: Date | null;
  onClose: () => void;
}

function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromInputValue(value: string): Date {
  return new Date(value);
}

export function GroupSessionModal(props: GroupSessionModalProps) {
  const { t } = useTranslation();
  const { clinicId, mode, sessionId, initialStart, onClose } = props;

  const { data: existing, isLoading: loadingExisting } = useGroupSession(
    mode === 'edit' ? sessionId : null,
  );
  const { data: participants } = useGroupSessionParticipants(mode === 'edit' ? sessionId : null);
  const { data: members } = useClinicMembers(clinicId);
  const { data: roles, isSuccess: rolesReady } = useClinicRoles(clinicId);
  const { data: clinic } = useClinic(clinicId);

  const create = useCreateGroupSession();
  const update = useUpdateGroupSession();
  const deleteSession = useDeleteGroupSession();
  const addParticipant = useAddParticipant();
  const removeParticipant = useRemoveParticipant();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clinicianId, setClinicianId] = useState<string | null>(null);
  const clinicianOptions = useMemo(() => {
    const all = members ?? [];
    if (!rolesReady) return all;
    return withStickySelectedMembers(filterPractitionerMembers(all, roles ?? []), all, [
      clinicianId,
    ]);
  }, [members, roles, rolesReady, clinicianId]);
  const [startsAt, setStartsAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [maxParticipants, setMaxParticipants] = useState(20);
  const [minParticipants, setMinParticipants] = useState(1);
  const [pricePerSpotCents, setPricePerSpotCents] = useState(0);
  const [color, setColor] = useState('#6366F1');
  const [status, setStatus] = useState<GroupSessionStatus>('scheduled');
  const [notes, setNotes] = useState('');

  const [showAddParticipant, setShowAddParticipant] = useState(false);

  const selectedCivilDate = startsAt ? civilDateFromLocalDate(fromInputValue(startsAt)) : null;
  const { data: hourExceptions } = useClinicHoursExceptions(
    clinicId,
    selectedCivilDate,
    selectedCivilDate,
  );
  const hoursCheck = useMemo(() => {
    if (!startsAt) {
      return { outsideOpeningHours: false, closedException: false, blockClosedDate: false };
    }
    const start = fromInputValue(startsAt);
    const end = new Date(start.getTime() + durationMinutes * 60_000);
    const weeklyHours = parseWeeklyHours(clinic?.business_hours);
    const resolved = resolveClinicHoursForLocalDate({
      date: start,
      weeklyHours,
      exception: exceptionByDate(hourExceptions).get(civilDateFromLocalDate(start)) ?? null,
    });
    const closedException = isClosedHoursException(resolved);
    const originalDate = existing?.starts_at
      ? civilDateFromLocalDate(new Date(existing.starts_at))
      : null;
    return {
      outsideOpeningHours: !localIntervalFitsResolvedHours(resolved, start, end),
      closedException,
      blockClosedDate:
        closedException && (mode === 'create' || originalDate !== civilDateFromLocalDate(start)),
    };
  }, [
    startsAt,
    durationMinutes,
    clinic?.business_hours,
    hourExceptions,
    existing?.starts_at,
    mode,
  ]);
  const outsideOpeningHours = hoursCheck.outsideOpeningHours;
  const blockClosedDate = hoursCheck.blockClosedDate;

  // Hydrate state when editing
  useEffect(() => {
    if (mode !== 'edit' || !existing) return;
    setName(existing.name);
    setDescription(existing.description ?? '');
    setClinicianId(existing.clinician_id);
    setStartsAt(isoLocal(new Date(existing.starts_at)));
    setDurationMinutes(existing.duration_minutes);
    setMaxParticipants(existing.max_participants);
    setMinParticipants(existing.min_participants);
    setPricePerSpotCents(existing.price_per_spot_cents);
    setColor(existing.color);
    setStatus(existing.status);
    setNotes(existing.notes ?? '');
  }, [mode, existing]);

  // Initial slot for create mode
  useEffect(() => {
    if (mode !== 'create') return;
    const start = initialStart ?? new Date();
    setStartsAt(isoLocal(start));
  }, [mode, initialStart]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error(t('group_sessions.errors.name_required'));
      return;
    }
    if (blockClosedDate) {
      toast.error(t('appointments.day_hours.closed_date_cannot_book'));
      return;
    }

    const startsAtDate = fromInputValue(startsAt);
    const endsAtDate = new Date(startsAtDate.getTime() + durationMinutes * 60_000);

    try {
      if (mode === 'create') {
        await create.mutateAsync({
          clinic_id: clinicId,
          name: name.trim(),
          description: description.trim() || null,
          starts_at: startsAtDate.toISOString(),
          ends_at: endsAtDate.toISOString(),
          duration_minutes: durationMinutes,
          max_participants: maxParticipants,
          min_participants: minParticipants,
          price_per_spot_cents: pricePerSpotCents,
          color,
          clinician_id: clinicianId,
          notes: notes.trim() || null,
        });
        toast.success(t('group_sessions.created'));
      } else {
        await update.mutateAsync({
          id: sessionId!,
          name: name.trim(),
          description: description.trim() || null,
          starts_at: startsAtDate.toISOString(),
          ends_at: endsAtDate.toISOString(),
          duration_minutes: durationMinutes,
          max_participants: maxParticipants,
          min_participants: minParticipants,
          price_per_spot_cents: pricePerSpotCents,
          color,
          clinician_id: clinicianId,
          status,
          notes: notes.trim() || null,
        });
        toast.success(t('group_sessions.updated'));
      }
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.save_failed'));
    }
  }

  async function handleDelete() {
    if (!sessionId) return;
    if (!confirm(t('group_sessions.confirm_delete'))) return;
    try {
      await deleteSession.mutateAsync(sessionId);
      toast.success(t('group_sessions.deleted'));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.delete_failed'));
    }
  }

  async function handleRemoveParticipant(participantId: string) {
    if (!sessionId) return;
    if (!confirm(t('group_sessions.confirm_remove_participant'))) return;
    try {
      await removeParticipant.mutateAsync({ participantId, sessionId });
      toast.success(t('group_sessions.participant_removed'));
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.remove_participant_failed'));
    }
  }

  const title =
    mode === 'create' ? t('group_sessions.create_title') : t('group_sessions.edit_title');
  const availableSpots = existing
    ? existing.max_participants - (existing as any).current_participants
    : maxParticipants;

  if (mode === 'edit' && loadingExisting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-ds-scrim">
        <div className="flex items-center gap-2 text-ds-toast-ink">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ds-scrim p-4">
      <div className="relative w-full max-w-3xl rounded-lg bg-ds-card shadow-[var(--sh-md)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ds-line px-6 py-4">
          <h2 className="text-xl font-semibold text-ds-text">{title}</h2>
          <Button
            type="button"
            variant="tertiary"
            size="icon"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="ds-field mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.description')}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="ds-field mt-1"
              />
            </div>

            {/* Date/Time and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('group_sessions.starts_at')} *
                </label>
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  required
                  className="ds-field mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('group_sessions.duration_minutes')} *
                </label>
                <input
                  type="number"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 60)}
                  min={15}
                  max={SERVICE_DURATION_MAX}
                  required
                  className="ds-field mt-1"
                />
              </div>
            </div>
            {blockClosedDate && (
              <div className="flex gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint p-2 text-ds-amber-ink">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-sm">{t('appointments.day_hours.closed_date_cannot_book')}</p>
              </div>
            )}
            {outsideOpeningHours && !blockClosedDate && (
              <div className="flex gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint p-2 text-ds-amber-ink">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-sm">{t('appointments.day_hours.outside_hours_warning')}</p>
              </div>
            )}

            {/* Capacity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('group_sessions.max_participants')} *
                </label>
                <input
                  type="number"
                  value={maxParticipants}
                  onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 1)}
                  min={1}
                  required
                  className="ds-field mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('group_sessions.min_participants')} *
                </label>
                <input
                  type="number"
                  value={minParticipants}
                  onChange={(e) => setMinParticipants(parseInt(e.target.value) || 1)}
                  min={1}
                  max={maxParticipants}
                  required
                  className="ds-field mt-1"
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.price_per_spot')}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="number"
                  value={pricePerSpotCents / 100}
                  onChange={(e) =>
                    setPricePerSpotCents(Math.round((parseFloat(e.target.value) || 0) * 100))
                  }
                  min={0}
                  step={0.01}
                  className="ds-field w-full"
                />
                <span className="text-sm text-ds-muted">DKK</span>
              </div>
            </div>

            {/* Clinician */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.clinician')}
              </label>
              <select
                value={clinicianId ?? ''}
                onChange={(e) => setClinicianId(e.target.value || null)}
                className="ds-field mt-1"
              >
                <option value="">{t('group_sessions.no_clinician')}</option>
                {clinicianOptions.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.color')}
              </label>
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="mt-1 h-10 w-20 rounded-md border border-ds-field bg-ds-card"
              />
            </div>

            {/* Status (edit mode only) */}
            {mode === 'edit' && (
              <div>
                <label className="block text-sm font-medium text-ds-text">
                  {t('group_sessions.status')}
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as GroupSessionStatus)}
                  className="ds-field mt-1"
                >
                  <option value="scheduled">{t('group_sessions.status_scheduled')}</option>
                  <option value="completed">{t('group_sessions.status_completed')}</option>
                  <option value="cancelled">{t('group_sessions.status_cancelled')}</option>
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-ds-text">
                {t('group_sessions.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="ds-field mt-1"
              />
            </div>

            {/* Participants (edit mode only) */}
            {mode === 'edit' && existing && (
              <div className="rounded-lg border border-ds-line p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-ds-text">
                    <Users className="h-4 w-4" />
                    {t('group_sessions.participants')} ({(existing as any).current_participants} /{' '}
                    {existing.max_participants})
                  </h3>
                  <Button type="button" onClick={() => setShowAddParticipant(true)}>
                    <UserPlus />
                    {t('group_sessions.add_participant')}
                  </Button>
                </div>

                {participants && participants.length > 0 ? (
                  <div className="space-y-2">
                    {participants.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between rounded-md bg-ds-sunk p-2"
                      >
                        <div>
                          <div className="text-sm font-medium text-ds-text">{p.patient_name}</div>
                          {p.patient_email && (
                            <div className="text-xs text-ds-muted">{p.patient_email}</div>
                          )}
                          <div className="text-xs text-ds-muted">
                            {p.spots} {p.spots === 1 ? 'spot' : 'spots'} •{' '}
                            {p.payment_amount_cents
                              ? `${p.payment_amount_cents / 100} DKK`
                              : 'No payment'}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="danger"
                          size="icon"
                          onClick={() => handleRemoveParticipant(p.id)}
                          aria-label={t('common.delete')}
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-sm text-ds-muted">
                    {t('group_sessions.no_participants')}
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-ds-line px-6 py-4">
          <div>
            {mode === 'edit' && (
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={deleteSession.isPending}
              >
                {deleteSession.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                {t('group_sessions.delete')}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={create.isPending || update.isPending || blockClosedDate}
            >
              {create.isPending || update.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {mode === 'create' ? t('group_sessions.create') : t('group_sessions.save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Add Participant Modal */}
      {showAddParticipant && sessionId && (
        <AddParticipantModal
          clinicId={clinicId}
          sessionId={sessionId}
          availableSpots={availableSpots}
          pricePerSpotCents={pricePerSpotCents}
          onClose={() => setShowAddParticipant(false)}
          onAdd={addParticipant.mutateAsync}
        />
      )}
    </div>
  );
}

interface AddParticipantModalProps {
  clinicId: string;
  sessionId: string;
  availableSpots: number;
  pricePerSpotCents: number;
  onClose: () => void;
  onAdd: (input: any) => Promise<any>;
}

function AddParticipantModal({
  clinicId,
  sessionId,
  availableSpots,
  pricePerSpotCents,
  onClose,
  onAdd,
}: AddParticipantModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const pickerQuery = usePatientPicker(clinicId, searchQuery);
  const [selectedPatient, setSelectedPatient] = useState<PatientPickerItem | null>(null);
  const [spots, setSpots] = useState(1);
  const [isPending, setIsPending] = useState(false);

  async function handleAdd() {
    if (!selectedPatient) {
      toast.error(t('group_sessions.errors.select_patient'));
      return;
    }
    setIsPending(true);
    try {
      await onAdd({
        clinic_id: clinicId,
        group_session_id: sessionId,
        patient_id: selectedPatient.id,
        spots,
        payment_amount_cents: pricePerSpotCents * spots,
        payment_currency: 'DKK',
        payment_status: null,
      });
      toast.success(t('group_sessions.participant_added'));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('group_sessions.errors.add_participant_failed'));
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ds-scrim p-4">
      <div className="w-full max-w-md rounded-lg bg-ds-card p-6 shadow-[var(--sh-md)]">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-ds-text">
            {t('group_sessions.add_participant')}
          </h3>
          <Button
            type="button"
            variant="tertiary"
            size="icon"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('group_sessions.select_patient')} *
            </label>
            <div className="mt-1">
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('common.search')}
                trailingIcon={
                  pickerQuery.isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : undefined
                }
              />
            </div>
            {(pickerQuery.data ?? []).length > 0 && !selectedPatient && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-ds-line">
                {(pickerQuery.data ?? []).map((p: PatientPickerItem) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(p);
                      setSearchQuery('');
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-ds-row-hover"
                  >
                    <div className="text-sm font-medium text-ds-text">
                      {p.first_name} {p.last_name}
                    </div>
                    {(p.email ||
                      formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)) && (
                      <div className="text-xs text-ds-muted">
                        {[p.email, formatPatientTelephoneNumbers(p.telephone_subscribers, p.phone)]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="mt-2 flex items-center justify-between rounded-md bg-ds-green-tint p-2">
                <div className="text-sm font-medium text-ds-green-ink">
                  {selectedPatient.first_name} {selectedPatient.last_name}
                </div>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  onClick={() => setSelectedPatient(null)}
                  aria-label={t('common.delete')}
                >
                  <X />
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-ds-text">
              {t('group_sessions.spots')} *
            </label>
            <input
              type="number"
              value={spots}
              onChange={(e) =>
                setSpots(Math.max(1, Math.min(availableSpots, parseInt(e.target.value) || 1)))
              }
              min={1}
              max={availableSpots}
              className="ds-field mt-1"
            />
            <div className="mt-1 text-xs text-ds-muted">
              {t('group_sessions.available_spots')}: {availableSpots}
            </div>
          </div>

          <div className="rounded-md border border-ds-teal-line bg-ds-teal-tint p-3">
            <div className="text-sm text-ds-teal">
              {t('group_sessions.total_price')}: {((pricePerSpotCents * spots) / 100).toFixed(2)}{' '}
              DKK
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleAdd} disabled={!selectedPatient || isPending}>
            {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('group_sessions.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}
